import { useSyncExternalStore } from 'react';
import { AVATAR_IDS, type AvatarId } from '@gmp/shared-types/browser';
import { SoundManager, readAloud } from '@gmp/shared-ui';
import { store } from '../net/storage';

export interface Prefs {
  nickname: string;
  avatar: AvatarId;
  muted: boolean;
  volume: number;
  reducedMotion: boolean;
  /** Accessibility: read prompts and choices aloud on this device. */
  readAloud: boolean;
  /** Read-aloud voice (null = best natural-sounding voice for the device language). */
  voiceURI: string | null;
  /** Read-aloud speed, 0.8–1.2. */
  speechRate: number;
}

const KEY = 'gmp.prefs';
const systemReduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
let prefs: Prefs = {
  nickname: '',
  avatar: AVATAR_IDS[Math.floor(Math.random() * AVATAR_IDS.length)]!,
  muted: false,
  volume: 0.7,
  reducedMotion: !!systemReduced,
  readAloud: false,
  voiceURI: null,
  speechRate: 1,
  ...store.get<Partial<Prefs>>(KEY),
};
const listeners = new Set<() => void>();

export const sound = new SoundManager();
sound.muted = prefs.muted;
sound.volume = prefs.volume;
const syncReadAloud = () => readAloud.configure({ enabled: prefs.readAloud, voiceURI: prefs.voiceURI, rate: prefs.speechRate });
syncReadAloud();

export function setPrefs(patch: Partial<Prefs>) {
  prefs = { ...prefs, ...patch };
  store.set(KEY, prefs);
  sound.muted = prefs.muted;
  sound.volume = prefs.volume;
  syncReadAloud();
  for (const l of listeners) l();
}

export function getPrefs(): Prefs {
  return prefs;
}

export function usePrefs(): Prefs {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => prefs,
  );
}
